import type {
  PoliticalMatchAnswerValue,
  PoliticalMatchAxis,
  PoliticalMatchCandidateMatch,
  PoliticalMatchMode,
  PoliticalMatchQuestion,
} from '@/models/political-match';
import {
  politicalMatchStoryTable,
  type PoliticalMatchStoryEconomicValue,
  type PoliticalMatchStoryPersonalValue,
} from '@/data/matchPoliticoMatches';

const answerLabels: Record<Exclude<PoliticalMatchAnswerValue, null> | 'null', string> = {
  [-2]: 'Discordo totalmente',
  [-1]: 'Discordo em parte',
  0: 'Neutro',
  1: 'Concordo em parte',
  2: 'Concordo totalmente',
  null: 'Sem posição suficiente',
};

const answerWeights: Record<Exclude<PoliticalMatchAnswerValue, null>, number> = {
  [-2]: -2,
  [-1]: -1,
  0: 0,
  1: 1,
  2: 2,
};

export function getPoliticalMatchAxisLabel(axis: PoliticalMatchAxis): string {
  return axis === 'economic' ? 'Dinheiro e trabalho' : 'Vida pessoal';
}

export function getPoliticalMatchDirectionLabel(axis: PoliticalMatchAxis, direction: 1 | -1): string {
  if (axis === 'economic') {
    return direction === 1 ? 'Concordar puxa para menos Estado' : 'Concordar puxa para mais Estado';
  }

  return direction === 1 ? 'Concordar puxa para mais liberdade' : 'Concordar puxa para menos liberdade';
}

export function getPoliticalMatchAnswerLabel(answer: PoliticalMatchAnswerValue): string {
  return answer === null ? answerLabels.null : answerLabels[answer];
}

export function buildPoliticalMatchQuestionPlan(questions: PoliticalMatchQuestion[], mode: PoliticalMatchMode | null): PoliticalMatchQuestion[] {
  if (!mode) {
    return [];
  }

  if (mode === 'quick') {
    return questions.filter((question) => question.isQuick);
  }

  return questions;
}

export function calculatePoliticalMatchAxisScore(
  questions: PoliticalMatchQuestion[],
  answers: Record<string, PoliticalMatchAnswerValue>,
  axis: PoliticalMatchAxis,
): number | null {
  let weightedScore = 0;
  let totalWeight = 0;

  for (const question of questions) {
    if (question.axis !== axis) {
      continue;
    }

    const answer = answers[question.id];

    if (answer === null || answer === undefined) {
      continue;
    }

    weightedScore += answerWeights[answer] * question.direction * question.weight;
    totalWeight += question.weight;
  }

  if (totalWeight === 0) {
    return null;
  }

  return Number((weightedScore / totalWeight).toFixed(2));
}

function scoreToDescriptor(score: number | null, positive: string, negative: string, center: string): string | null {
  if (score === null) {
    return null;
  }

  if (score >= 0.75) {
    return positive;
  }

  if (score <= -0.75) {
    return negative;
  }

  return center;
}

export function describePoliticalMatchAlignment(economicScore: number | null, personalScore: number | null): string {
  const descriptors = [
    scoreToDescriptor(economicScore, 'mais mercado', 'mais Estado', 'no meio'),
    scoreToDescriptor(personalScore, 'mais liberdade', 'mais controle', 'no meio'),
  ].filter((descriptor): descriptor is string => descriptor !== null);

  const nonCenterDescriptors = descriptors.filter((descriptor) => descriptor !== 'no meio');

  if (descriptors.length === 0) {
    return 'Sem posição suficiente';
  }

  if (nonCenterDescriptors.length === 0) {
    return 'Centro';
  }

  if (nonCenterDescriptors.length === 1) {
    return nonCenterDescriptors[0]!;
  }

  return nonCenterDescriptors.join(' e ');
}

export type PoliticalMatchStoryCopy = {
  eyebrow: string;
  title: string;
  description: string[];
};

function formatPoliticalMatchStoryLead(matchName: string, lead: string): string {
  const normalizedLead = lead.trim().replace(/^([A-ZÀ-Ý])/u, (letter) => letter.toLowerCase());

  return `Como ${matchName}... ${normalizedLead}`;
}

function classifyStoryEconomicScore(score: number | null): PoliticalMatchStoryEconomicValue {
  if (score === null) {
    return 'centro';
  }

  if (score >= 0.75) {
    return 'mais_liberdade_economica';
  }

  if (score <= -0.75) {
    return 'menos_liberdade_economica';
  }

  return 'centro';
}

function classifyStoryPersonalScore(score: number | null): PoliticalMatchStoryPersonalValue {
  if (score === null) {
    return 'centro';
  }

  if (score >= 0.75) {
    return 'mais_liberdade_pessoal';
  }

  if (score <= -0.75) {
    return 'menos_liberdade_pessoal';
  }

  return 'centro';
}

export function buildPoliticalMatchStoryCopy(economicScore: number | null, personalScore: number | null): PoliticalMatchStoryCopy {
  const economicBand = classifyStoryEconomicScore(economicScore);
  const personalBand = classifyStoryPersonalScore(personalScore);
  const match = politicalMatchStoryTable[personalBand][economicBand];

  return {
    eyebrow: 'SEU MATCH POLÍTICO',
    title: match.nome,
    description: match.lead.map((lead, index) => (index === 0 ? formatPoliticalMatchStoryLead(match.nome, lead) : lead)),
  };
}

export function buildPoliticalMatchSummary(
  questions: PoliticalMatchQuestion[],
  answers: Record<string, PoliticalMatchAnswerValue>,
): {
  economicScore: number | null;
  personalScore: number | null;
  alignmentLabel: string;
  answeredQuestions: number;
  totalQuestions: number;
} {
  const economicScore = calculatePoliticalMatchAxisScore(questions, answers, 'economic');
  const personalScore = calculatePoliticalMatchAxisScore(questions, answers, 'personal');
  const answeredQuestions = Object.values(answers).filter((answer) => answer !== null && answer !== undefined).length;

  return {
    economicScore,
    personalScore,
    alignmentLabel: describePoliticalMatchAlignment(economicScore, personalScore),
    answeredQuestions,
    totalQuestions: questions.length,
  };
}

export function clampPoliticalMatchDiagramCoordinate(score: number | null, axis: 'x' | 'y'): number {
  const raw = score === null ? 50 : axis === 'x' ? ((score + 2) / 4) * 100 : ((2 - score) / 4) * 100;

  return Math.max(8, Math.min(92, raw));
}

export function calculatePoliticalMatchDistance(
  userEconomicScore: number | null,
  userPersonalScore: number | null,
  candidateEconomicScore: number | null,
  candidatePersonalScore: number | null,
): number | null {
  if (
    userEconomicScore === null ||
    userPersonalScore === null ||
    candidateEconomicScore === null ||
    candidatePersonalScore === null
  ) {
    return null;
  }

  return Math.hypot(candidateEconomicScore - userEconomicScore, candidatePersonalScore - userPersonalScore);
}

export const politicalMatchCloseCandidateDistanceLimit = 1.3;

export function sortPoliticalMatchMatchesByDistance(
  matches: PoliticalMatchCandidateMatch[],
  userEconomicScore: number | null,
  userPersonalScore: number | null,
): PoliticalMatchCandidateMatch[] {
  return [...matches].sort((left, right) => {
    const leftDistance = calculatePoliticalMatchDistance(
      userEconomicScore,
      userPersonalScore,
      left.position.economicScore,
      left.position.personalScore,
    );
    const rightDistance = calculatePoliticalMatchDistance(
      userEconomicScore,
      userPersonalScore,
      right.position.economicScore,
      right.position.personalScore,
    );

    if (leftDistance === null && rightDistance === null) {
      const nameComparison = left.candidate.name.localeCompare(right.candidate.name, 'pt-BR');

      if (nameComparison !== 0) {
        return nameComparison;
      }

      return left.candidate.id.localeCompare(right.candidate.id, 'pt-BR');
    }

    if (leftDistance === null) {
      return 1;
    }

    if (rightDistance === null) {
      return -1;
    }

    if (leftDistance !== rightDistance) {
      return leftDistance - rightDistance;
    }

    const nameComparison = left.candidate.name.localeCompare(right.candidate.name, 'pt-BR');

    if (nameComparison !== 0) {
      return nameComparison;
    }

    return left.candidate.id.localeCompare(right.candidate.id, 'pt-BR');
  });
}

export function describePoliticalMatchAxisPosition(axis: PoliticalMatchAxis, score: number | null): string {
  if (axis === 'economic') {
    return scoreToDescriptor(score, 'mais mercado', 'mais Estado', 'no meio') ?? 'Sem posição suficiente';
  }

  return scoreToDescriptor(score, 'mais liberdade', 'mais controle', 'no meio') ?? 'Sem posição suficiente';
}

export function formatPoliticalMatchScore(score: number | null): string {
  if (score === null) {
    return 'Sem posição suficiente';
  }

  return score > 0 ? `+${score.toFixed(2).replace('.', ',')}` : score.toFixed(2).replace('.', ',');
}
